import { ChangeEvent, FocusEvent, MouseEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
// mui
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListSubheader,
  Popover,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// api
import { getCoupons, verifyCoupon } from '@/features/coupons/api';
// hooks + utils
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { fCurrency } from '@/utils/formatNumber';
// redux
import { useDispatch, useSelector } from '@/stores';
import { setCouponObject } from '@/features/bookings-management/slices';
// types
import { Coupon } from '@/features/coupons/types';
import { ServiceVariant } from '@/types';

const ADMIN_PLATFORM_NAME = 'admin dashboard';

const formatCouponValue = (coupon: Coupon) => {
  const numericValue = Number(coupon.value);

  if (!Number.isFinite(numericValue)) {
    return coupon.value;
  }

  if (coupon.value_type?.toLowerCase() === 'percentage') {
    return `${numericValue}%`;
  }

  return fCurrency(numericValue);
};

export default function Discount() {
  const booking = useSelector((state) => state.booking);
  const {
    selectedServiceVariant,
    service_variant_id: serviceVariantIdFromState,
    service_variant: serviceVariantFromBooking,
    client,
    cleanDetails,
  } = booking;

  const [isActive, setIsActive] = useState<boolean>(true);
  const [couponCode, setCouponCode] = useState<string>(
    cleanDetails.couponObject?.code ?? ''
  );
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [couponListAnchor, setCouponListAnchor] = useState<HTMLElement | null>(
    null
  );

  const dispatch = useDispatch();
  const { errorMsg } = useSnackbarMsg();

  const serviceVariantId =
    (selectedServiceVariant as ServiceVariant)?.id ??
    serviceVariantIdFromState ??
    serviceVariantFromBooking?.id ??
    null;

  const clientId = client?.id ? Number(client.id) : null;

  const {
    data: availableCoupons = [],
    isLoading: isCouponsLoading,
  } = useQuery({
    queryKey: ['coupons', 'admin-dashboard', clientId],
    queryFn: getCoupons,
    enabled: isActive,
    select: (coupons) =>
      coupons
        .filter((coupon) =>
          (coupon.coupon_platforms ?? []).some(
            (platform) =>
              platform.name?.trim().toLowerCase() === ADMIN_PLATFORM_NAME
          )
        )
        .filter((coupon) => {
          if (!coupon.active) return false;

          const now = Date.now();
          const startOk = coupon.start_date
            ? Date.parse(coupon.start_date) <= now
            : true;
          const endOk = coupon.end_date
            ? Date.parse(coupon.end_date) >= now
            : true;

          return startOk && endOk;
        })
        .filter((coupon) => {
          if (!coupon.user_id) return true;
          if (!clientId) return false;

          return Number(coupon.user_id) === clientId;
        })
        .sort((a, b) => a.code.localeCompare(b.code)),
  });

  const groupedCoupons = useMemo(() => {
    if (!availableCoupons.length) return [] as { title: string; coupons: Coupon[] }[];

    const userSpecificCoupons = availableCoupons.filter((coupon) => Boolean(coupon.user_id));
    const systemWideCoupons = availableCoupons.filter((coupon) => !coupon.user_id);

    const groups: { title: string; coupons: Coupon[] }[] = [];

    if (userSpecificCoupons.length) {
      const customerName = [client?.first_name, client?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();

      groups.push({
        title: customerName || client?.email || 'User coupons',
        coupons: userSpecificCoupons,
      });
    }

    if (systemWideCoupons.length) {
      groups.push({ title: 'System coupons', coupons: systemWideCoupons });
    }

    return groups;
  }, [availableCoupons, client?.email, client?.first_name, client?.last_name]);

  const appliedCouponCode = cleanDetails.couponObject?.code?.trim() ?? '';
  const trimmedInput = couponCode.trim();
  const isCouponListOpen = Boolean(couponListAnchor);

  const handleToggleActive = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsActive(checked);
    if (!checked) {
      setCouponCode('');
      setCouponListAnchor(null);
    }
  };

  const handleOpenCouponList = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement> | MouseEvent<HTMLElement>
  ) => {
    if (!isActive) return;
    const target = event.currentTarget as HTMLElement;
    setCouponListAnchor(target);
  };

  const handleCloseCouponList = () => {
    setCouponListAnchor(null);
  };

  const handleApplyCoupon = async () => {
    if (!isActive) return;

    if (!serviceVariantId) {
      errorMsg('Select a service first');
      return;
    }

    if (!trimmedInput) {
      errorMsg('Enter a coupon code to apply');
      return;
    }

    try {
      setButtonLoading(true);

      const normalizedInput = trimmedInput.toLowerCase();
      const matchedCoupon = availableCoupons.find(
        (coupon) => coupon.code?.toLowerCase() === normalizedInput
      );

      const couponToApply =
        matchedCoupon ??
        (await verifyCoupon({
          service_variant_id: serviceVariantId,
          couponCode: trimmedInput,
          platform: 'ADMIN_DASHBOARD',
        }));

      dispatch(setCouponObject(couponToApply));
      setCouponCode('');
      setCouponListAnchor(null);
    } catch (error) {
      errorMsg(error, 'There was an error while applying coupon code');
    } finally {
      setButtonLoading(false);
    }
  };

  const couponListContent = (
    <Box sx={{ width: 360, maxWidth: '100%' }}>
      {!client && (
        <FormHelperText sx={{ mb: 1 }}>
          Select a customer to view their personalised coupons.
        </FormHelperText>
      )}

      {isCouponsLoading ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading available coupons...
          </Typography>
        </Stack>
      ) : groupedCoupons.length ? (
        <List dense disablePadding sx={{ width: '100%', maxHeight: 260, overflowY: 'auto' }}>
          {groupedCoupons.map((group) => (
            <li key={group.title}>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                <ListSubheader disableSticky sx={{ px: 0 }}>
                  {group.title}
                </ListSubheader>
                {group.coupons.map((coupon) => {
                  const isSelected =
                    coupon.code?.toLowerCase() === appliedCouponCode.toLowerCase();

                  const handleSelectCoupon = () => {
                    setCouponCode(coupon.code ?? '');
                    setCouponListAnchor(null);
                  };

                  return (
                    <ListItem key={coupon.id} disablePadding sx={{ py: 0.5 }}>
                      <ListItemButton
                        onClick={handleSelectCoupon}
                        disabled={buttonLoading}
                        selected={isSelected}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ width: '100%' }}
                          spacing={1}
                        >
                          <Stack spacing={0.25} maxWidth="70%">
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {coupon.code}
                            </Typography>
                            {coupon.description?.trim() && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {coupon.description.trim()}
                              </Typography>
                            )}
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={formatCouponValue(coupon)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {coupon.user_id && (
                              <Chip label="User" size="small" variant="outlined" />
                            )}
                          </Stack>
                        </Stack>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </ul>
            </li>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No active coupons available for this booking.
        </Typography>
      )}
    </Box>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          control={<Switch checked={isActive} onChange={handleToggleActive} />}
          label="Coupon"
          sx={{ width: '23%' }}
        />

        {isActive && (
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Coupon code"
              onChange={(e) => setCouponCode(e.target.value)}
              value={couponCode}
              size="small"
              sx={{ width: 200 }}
              onFocus={handleOpenCouponList}
              onClick={handleOpenCouponList}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleApplyCoupon();
                }
              }}
              helperText={
                appliedCouponCode
                  ? `Applied coupon: ${appliedCouponCode}`
                  : 'Enter a coupon code or select from the list.'
              }
            />

            <LoadingButton
              variant="contained"
              size="small"
              loading={buttonLoading}
              onClick={handleApplyCoupon}
              disabled={!trimmedInput || buttonLoading}
            >
              Apply
            </LoadingButton>
          </Stack>
        )}
      </Stack>

      {isActive && (
        <Popover
          open={isCouponListOpen}
          anchorEl={couponListAnchor}
          onClose={handleCloseCouponList}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          PaperProps={{ sx: { p: 2, width: 'auto' } }}
        >
          {couponListContent}
        </Popover>
      )}
    </Stack>
  );
}
